/**
 * Reading ROC data from a pasted "score, class" table.
 *
 * The first column is the test score, the second the reference-standard class. A row with
 * a missing score or class is set aside; a score that is not a number is reported with its
 * line so the reader can fix it.
 */

import { parseNumberInput } from "@/lib/parse-number";

export interface ScoredRows {
  scores: number[];
  classes: string[];
  dropped: number;
  /** The first score cell that was not a number, with its 1-based line. */
  unreadable: { line: number; text: string } | null;
}

export function readScoreRows(
  rows: readonly (readonly string[])[],
  lines: readonly number[]
): ScoredRows {
  const result: ScoredRows = {
    scores: [],
    classes: [],
    dropped: 0,
    unreadable: null,
  };
  for (const [index, row] of rows.entries()) {
    const [scoreCell = "", classCell = ""] = row;
    if (scoreCell === "" || classCell === "") {
      result.dropped += 1;
      continue;
    }
    const score = parseNumberInput(scoreCell);
    if (score === null) {
      result.unreadable ??= {
        line: lines[index] ?? index + 1,
        text: scoreCell,
      };
      continue;
    }
    result.scores.push(score);
    result.classes.push(classCell);
  }
  return result;
}

const POSITIVE_WORDS = new Set([
  "1",
  "true",
  "yes",
  "y",
  "positive",
  "pos",
  "case",
  "cases",
  "disease",
  "diseased",
  "event",
  "+",
]);

/** The class label most likely to mean "positive": a common word, else the larger code. */
export function guessPositiveLabel(labels: readonly string[]): string {
  const match = labels.find((label) => POSITIVE_WORDS.has(label.toLowerCase()));
  if (match !== undefined) {
    return match;
  }
  const sorted = [...labels].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
  return sorted.at(-1) ?? "";
}

export function distinctClasses(classes: readonly string[]): string[] {
  return [...new Set(classes)];
}

export function splitByClass(
  scores: readonly number[],
  classes: readonly string[],
  positiveLabel: string
): { positives: number[]; negatives: number[] } {
  const positives: number[] = [];
  const negatives: number[] = [];
  for (const [index, score] of scores.entries()) {
    (classes[index] === positiveLabel ? positives : negatives).push(score);
  }
  return { positives, negatives };
}
